/**
 * Recovers a Cloudinary `public_id` from a delivery URL.
 *
 * **A fallback, not the primary source.** The upload response carries the
 * `public_id` directly and that is what should be stored. This exists because
 * a row can reach us without one: MYD-462 lost it in the handoff from the
 * image library into a note, and any future path that forgets to carry it will
 * lose it the same silent way, since every field on `LibraryImageRef` is
 * optional.
 *
 * The `public_id` is the only handle back to the stored asset. A row that
 * cannot name its own asset cannot take part in any cleanup, so recovering it
 * from the URL is worth doing even though the URL is a slightly awkward place
 * to read it from.
 *
 * Cloudinary delivery URLs look like:
 *
 *   https://res.cloudinary.com/<cloud>/image/upload/<transforms>/v<version>/<public_id>.<ext>
 *
 * The transform segment is optional and the version segment is not always
 * present, which is what makes this fiddlier than a split.
 */

/**
 * @param url A Cloudinary delivery URL.
 * @returns The `public_id`, including any folder path, or `null` when the URL
 *   is not a Cloudinary delivery URL or has no recoverable id.
 */
export const cloudinaryPublicId = (url?: string | null): string | null => {
	if (!url || typeof url !== 'string') return null

	// Everything after the delivery verb. `/upload/` covers uploaded assets;
	// fetched and privately-delivered assets use different verbs and are not
	// ours to name.
	const afterUpload = url.split('?')[0].split('#')[0].match(/\/upload\/(.+)$/)
	if (!afterUpload) return null

	let rest = afterUpload[1]

	// Drop the version segment and anything before it. Transformations always
	// sit before the version, so finding the version removes them too, however
	// many there are.
	const versioned = rest.match(/(?:^|\/)v\d+\/(.+)$/)
	if (versioned) {
		rest = versioned[1]
	} else {
		// No version segment. Any leading transformation segments have to be
		// dropped by shape instead: they are comma-separated `k_v` pairs, which
		// a folder name would not be.
		const segments = rest.split('/')
		while (segments.length > 1 && /^[a-z]{1,3}_[^/]*$/.test(segments[0])) {
			segments.shift()
		}
		rest = segments.join('/')
	}

	// Strip the format extension. Folders may contain dots, so only a trailing
	// short alphanumeric run on the LAST segment counts.
	const publicId = rest.replace(/\.[A-Za-z0-9]{2,5}$/, '')

	return publicId.length > 0 ? publicId : null
}

export default cloudinaryPublicId
